import { get, post } from '../utils/FetchV2.util';

export type RfcIdentityPayload = {
	nombre: string;
	apellido_paterno: string;
	apellido_materno: string;
};

export type RfcValidationErrorCode =
	| 'INVALID_CHECK_DIGIT'
	| 'NAME_MISMATCH'
	| 'RATE_LIMIT'
	| 'UNKNOWN';

export type RfcValidationResult = {
	valid: boolean;
	code?: RfcValidationErrorCode;
	reason?: string;
};

export type GenerateRfcResult = {
	rfc: string;
};

const RATE_LIMIT_STATUS = 429;

const extractRateLimitError = (error: unknown): RfcValidationResult | null => {
	const status = (error as { response?: { status?: number } })?.response
		?.status;
	if (status === RATE_LIMIT_STATUS) {
		return {
			valid: false,
			code: 'RATE_LIMIT',
			reason:
				'Alcanzaste el límite diario de validaciones. Inténtalo mañana.',
		};
	}
	return null;
};

export const generateRfcService = async (
	payload: { curp: string } & RfcIdentityPayload,
): Promise<GenerateRfcResult> => {
	return post<GenerateRfcResult>({
		service: 'userManagementEngine',
		endpoint: '/user-account/web/rfc/generate',
		body: payload,
		skipAccountError: true,
	});
};

/** `verified` = el SAT confirmó el RFC · `unverified` = lo calculamos con la
 * CURP y el SAT no lo ha confirmado · `pending` = hay una consulta al SAT en
 * curso · `none` = no hay con qué armarlo. */
export type RfcPrefillStatus = 'verified' | 'unverified' | 'pending' | 'none';

export type RfcPrefillReason =
	| 'not_valid'
	| 'no_curp'
	| 'not_extracted'
	| 'extraction_failed'
	| 'no_rfc';

/** Contrato de user-management-engine: el RFC con el que se puede prellenar
 * el campo, resuelto del lado del servidor (taxpayer primero, luego la CURP
 * del onboarding o del lead). Solo lee: nunca dispara extracciones. */
export type RfcPrefillResponse = {
	rfc: string | null;
	status: RfcPrefillStatus;
	reason: RfcPrefillReason | null;
	source: 'taxpayer' | 'curp' | null;
	curp_origin: 'taxpayer' | 'onboarding' | 'lead' | null;
};

export const getRfcPrefillService = async (): Promise<RfcPrefillResponse> => {
	return get<RfcPrefillResponse>({
		service: 'userManagementEngine',
		endpoint: '/user-account/web/rfc/prefill',
	});
};

export const validateRfcService = async (
	payload: { rfc: string } & RfcIdentityPayload,
): Promise<RfcValidationResult> => {
	try {
		const response = await post<RfcValidationResult>({
			service: 'userManagementEngine',
			endpoint: '/user-account/web/rfc/validate',
			body: payload,
			skipAccountError: true,
		});
		return response;
	} catch (error) {
		const rateLimit = extractRateLimitError(error);
		if (rateLimit) return rateLimit;
		throw error;
	}
};

export const validateCurpService = async (
	payload: { curp: string } & RfcIdentityPayload,
): Promise<RfcValidationResult> => {
	try {
		const response = await post<RfcValidationResult>({
			service: 'userManagementEngine',
			endpoint: '/user-account/web/curp/validate',
			body: payload,
			skipAccountError: true,
		});
		return response;
	} catch (error) {
		const rateLimit = extractRateLimitError(error);
		if (rateLimit) return rateLimit;
		throw error;
	}
};

export const isRateLimitError = (error: unknown): boolean =>
	(error as { response?: { status?: number } })?.response?.status ===
	RATE_LIMIT_STATUS;
